import {
  useGetAlbumAnalyticsApiV1Suspense,
  useGetArtistAnalyticsApiV1Suspense,
  useGetCountryAnalyticsApiV1Suspense,
  useGetOverviewAnalyticsApiV1Suspense,
  useGetPlatformAnalyticsApiV1Suspense,
  useGetTrackAnalyticsApiV1Suspense,
} from "@/api/__generated__/api.schema";
import { DiffRatioDisplay } from "@/components/dashboard/diff-ratio-display";
import { LinearChart } from "@/components/dashboard/linear-chart";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { AlbumFlag } from "@/routes/custom-music/dsp-analytics/components/graphics/album-flag";
import { CountryFlag } from "@/routes/custom-music/dsp-analytics/components/graphics/country-flag";
import { PlatformFlag } from "@/routes/custom-music/dsp-analytics/components/graphics/platform-flag";
import { RevenueNumberedTable } from "@/routes/custom-music/dsp-analytics/components/graphics/revenue-numbered-table";
import { TrackFlag } from "@/routes/custom-music/dsp-analytics/components/graphics/track-flag";
import { useAnalyticsMonth } from "@/routes/custom-music/dsp-analytics/context/dsp-analytics-context";
import { getPreviousMonthRange } from "@/utils/date";
import { formatCurrency } from "@/utils/format";
import { formatMonthString } from "@/utils/time";
import { ShadowBox, Typography } from "@backpac/monica";
import { useMemo } from "react";

export const OverviewAnalytics = () => {
  return (
    <div className="flex flex-col gap-6">
      <OverviewAnalytics.Revenue />
      <OverviewAnalytics.Platform />
      <div className="grid grid-cols-2 gap-6">
        <OverviewAnalytics.Album />
        <OverviewAnalytics.Track />
        <OverviewAnalytics.Artist />
        <OverviewAnalytics.Country />
      </div>
    </div>
  );
};

OverviewAnalytics.Revenue = () => {
  const { formatedMonthRange, selectedMonthRange } = useAnalyticsMonth();
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { item: overview },
    },
  } = useGetOverviewAnalyticsApiV1Suspense(workspaceId, {
    from: formatMonthString(selectedMonthRange[0]),
    to: formatMonthString(selectedMonthRange[1]),
  });
  // previous revenue with the same length as the current revenue
  const {
    data: {
      data: { item: previousOverview },
    },
  } = useGetOverviewAnalyticsApiV1Suspense(workspaceId, {
    from: formatMonthString(getPreviousMonthRange(selectedMonthRange)[0]),
    to: formatMonthString(getPreviousMonthRange(selectedMonthRange)[1]),
  });

  const diffRatio = useMemo(() => {
    if (!overview || !previousOverview) return 0;

    return previousOverview.revenue === 0
      ? 0
      : ((overview.revenue - previousOverview.revenue) / Math.abs(previousOverview.revenue)) * 100;
  }, [previousOverview, overview]);

  const data = useMemo(() => {
    return (
      overview?.monthlyData
        .sort((a, b) => a.settlementMonth.localeCompare(b.settlementMonth)) // sort by date ascending
        .map((item) => ({
          date: item.settlementMonth,
          value: item.revenue,
        })) ?? []
    );
  }, [overview]);

  return (
    <ShadowBox className="flex flex-col bg-white p-6">
      <div className="flex flex-row justify-between items-center mb-2">
        <Typography size="md" font="unbounded" color="textPrimary">
          Revenue
        </Typography>
        <Typography size="xs" color="textMuted">
          {formatedMonthRange}
        </Typography>
      </div>
      <div className="flex flex-row justify-between items-center mb-9">
        <div className="flex gap-3 items-center">
          <Typography size="xl" color="textPrimary" weight={600}>
            {formatCurrency(overview?.revenue ?? 0)}
          </Typography>
          {diffRatio !== 0 && <DiffRatioDisplay diffRatio={diffRatio} />}
        </div>
      </div>
      <div className="flex h-44">
        <LinearChart data={data} formatValue={(v) => formatCurrency(v)} dateType="month" />
      </div>
    </ShadowBox>
  );
};

OverviewAnalytics.Platform = () => {
  const { selectedMonthRange } = useAnalyticsMonth();
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { items: analytics },
    },
  } = useGetPlatformAnalyticsApiV1Suspense(workspaceId, {
    from: formatMonthString(selectedMonthRange[0]),
    to: formatMonthString(selectedMonthRange[1]),
  });

  return (
    <RevenueNumberedTable
      title="Platform"
      tab="platform"
      analytics={analytics.slice(0, 10)}
      renderNameComponent={(item) => <PlatformFlag platform={item.name} />}
    />
  );
};

OverviewAnalytics.Album = () => {
  const { selectedMonthRange } = useAnalyticsMonth();
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { items: analytics },
    },
  } = useGetAlbumAnalyticsApiV1Suspense(workspaceId, {
    from: formatMonthString(selectedMonthRange[0]),
    to: formatMonthString(selectedMonthRange[1]),
  });

  return (
    <RevenueNumberedTable
      title="Album"
      tab="album"
      analytics={analytics.slice(0, 5)}
      renderNameComponent={(item) => (
        <AlbumFlag name={item.name} coverImageUrl={item.coverImageUrl} className="size-8 rounded-md" />
      )}
    />
  );
};

OverviewAnalytics.Artist = () => {
  const { selectedMonthRange } = useAnalyticsMonth();
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { items: analytics },
    },
  } = useGetArtistAnalyticsApiV1Suspense(workspaceId, {
    from: formatMonthString(selectedMonthRange[0]),
    to: formatMonthString(selectedMonthRange[1]),
  });

  return <RevenueNumberedTable title="Artist" tab="artist" analytics={analytics.slice(0, 5)} />;
};

OverviewAnalytics.Track = () => {
  const { selectedMonthRange } = useAnalyticsMonth();
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { items: analytics },
    },
  } = useGetTrackAnalyticsApiV1Suspense(workspaceId, {
    from: formatMonthString(selectedMonthRange[0]),
    to: formatMonthString(selectedMonthRange[1]),
  });

  return (
    <RevenueNumberedTable
      title="Track"
      tab="track"
      analytics={analytics.slice(0, 5)}
      renderNameComponent={(item) => (
        <TrackFlag name={item.name} coverImageUrl={item.coverImageUrl} className="size-8 rounded-md" />
      )}
    />
  );
};
OverviewAnalytics.Country = () => {
  const { selectedMonthRange } = useAnalyticsMonth();
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { items: analytics },
    },
  } = useGetCountryAnalyticsApiV1Suspense(workspaceId, {
    from: formatMonthString(selectedMonthRange[0]),
    to: formatMonthString(selectedMonthRange[1]),
  });

  return (
    <RevenueNumberedTable
      title="Country"
      tab="country"
      analytics={analytics.slice(0, 5)}
      renderNameComponent={(item) => <CountryFlag countryCode={item.name} />}
    />
  );
};
